import React, { JSX } from "react";
import { useTheme } from "styled-components";
import { Box } from "../box/Box";
import Spacer from "../spacer/Spacer";
import { ITheme, RecursiveCSS } from "../theme/GlobalTheme";

export type TButtons = typeof getDefaultButtons;
export type TButtonVariant = keyof TButtons;

const DISABLED_OPACITY = 0.55;

const defaultButton: React.CSSProperties & Record<"&:hover", React.CSSProperties> = {
	padding: "9px 16px",
	minHeight: "38px",
	borderRadius: "8px",
	cursor: "pointer",
	boxShadow: "0 1px 2px rgba(23, 23, 28, .12), inset 0 1px 0 rgba(255, 255, 255, .08)",
	transition: "background-color .16s cubic-bezier(.2,.8,.2,1), border-color .16s cubic-bezier(.2,.8,.2,1), box-shadow .16s cubic-bezier(.2,.8,.2,1), transform .08s ease",
	display: "inline-flex",
	alignItems: "center",
	position: "relative",
	gap: "8px",
	fontWeight: "600",
	userSelect: "none",
	fontSize: ".875rem",
	letterSpacing: "-0.005em",
	justifyContent: "center",
	whiteSpace: "nowrap",
	textOverflow: "ellipsis",
	overflow: "hidden",
	border: "1px solid transparent",
	fontFamily: "inherit",
	backgroundColor: "buttonBackgroundPrimary",
	color: "buttonTextPrimary",

	"&:hover": {
		backgroundColor: "buttonBackgroundPrimaryHover",
	},
	"&:active": { transform: "scale(.98)" },
} as never;

const secondary: RecursiveCSS = {
	...defaultButton,
	backgroundColor: "#FFFFFF",
	border: "1px solid #C6C6D0",
	boxShadow: "0 1px 2px rgba(23, 23, 28, .05)",
	color: "buttonTextSecondary",
	"&:hover": {
		backgroundColor: "#EEEEF2",
		borderColor: "#A9A9B5",
	},
};

const small: RecursiveCSS = {
	fontSize: ".8125rem",
	padding: "6px 12px",
	minHeight: "32px",
	borderRadius: "7px",
};

export const getDefaultButtons = {
	primary: { ...defaultButton },

	secondary: { ...secondary },

	empty: {
		...defaultButton,
		boxShadow: "none",
		backgroundColor: "transparent",
		"&:hover": {
			backgroundColor: "transparent",
		},
	},

	icon: {
		...defaultButton,
		//border: "1px solid buttonBackgroundSecondary",
		boxShadow: "none",
		backgroundColor: "transparent",
		"&:hover": {
			backgroundColor: "transparent", // "buttonBackgroundSecondary",
			//border: "1px solid buttonBackgroundPrimary",
		},
		borderRadius: "8px",
		padding: "0px",
		display: "flex",
	},

	primarySmall: { ...defaultButton, ...small },

	dangerSmall: {
		...defaultButton,
		...small,
		backgroundColor: "#C4320A",
		color: "#FFFFFF",
		"&:hover": {
			backgroundColor: "#A82A08",
		},
	},

	secondarySmall: {
		...secondary,
		...small,
	},

	emptySmall: { ...defaultButton },
};

interface IButtonProps {
	onClick: (e?: any) => void;
	children?: any;
	title?: string;
	variant?: TButtonVariant;
	sx?: React.CSSProperties & { [key: string]: any };
	iconRight?: JSX.Element;
	size?: "small" | "medium" | "large";
	disabled?: boolean;
	warning?: boolean;
	color?: string;
	decorator?: "form";
	icon?: JSX.Element;
	center?: boolean;
	ariaLabel?: string;
}

const Button = ({ onClick, icon, children, variant, sx, iconRight, disabled, title, center, ariaLabel }: IButtonProps) => {
	if (!variant && icon && !title && !children) variant = "icon";

	if (!variant) variant = "primary";

	let tsx = {} as React.CSSProperties;

	if (!children && title) children = title;
	let content = children;

	const theme = useTheme() as ITheme;
	if (theme && theme.buttons && theme.buttons[variant]) tsx = theme.buttons[variant];

	return (
		<Box
			element='button'
			type='button'
			disabled={disabled}
			aria-label={ariaLabel ?? title}
			sx={{
				...tsx, //variant
				...sx, // user styles
				pointerEvents: "auto",
				opacity: disabled ? DISABLED_OPACITY : 1,
				cursor: disabled ? "not-allowed" : "pointer",
			}}
			onClick={onClick}>
			{icon && (
				<Box
					element='span'
					sx={{
						alignItems: "center",
						lineHeight: "0px",
					}}>
					{icon}
				</Box>
			)}

			{content}

			{iconRight && (
				<>
					{!center && <Spacer grow element='span' />}
					<Box element='span' sx={{ lineHeight: "0px" }}>{iconRight}</Box>
				</>
			)}
		</Box>
	);
};

export default Button;
